import './DashboardPages.css';
import DashboardLayout from '../components/DashboardLayout';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ministriesApi } from '../api/ministries';
import { organizationsApi } from '../api/organizations';
import LoadingSpinner from '../components/LoadingSpinner';

function DashboardMinistries() {
	const [ministries, setMinistries] = useState([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [teams, setTeams] = useState([]);
	const [error, setError] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const { orgData, userData } = useAuth();
	const [formData, setFormData] = useState({
		name: '',
		description: '',
		teamId: '',
		isActive: true,
		inactiveMessage: '',
	});

	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const [ministriesData, teamsData] = await Promise.all([
					ministriesApi.getAll(orgData.id),
					organizationsApi.teams.getAll(orgData.id),
				]);
				setMinistries(ministriesData.ministries);
				setTeams(teamsData.teams);
			} catch (error) {
				console.error('Error fetching data:', error);
				setError('Failed to load data');
			} finally {
				setIsLoading(false);
			}
		};

		if (orgData?.id) {
			fetchData();
		}
	}, [orgData]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			setError('');
			await ministriesApi.create(orgData.id, {
				...formData,
				organizationId: orgData.id,
			});
			const data = await ministriesApi.getAll(orgData.id);
			setMinistries(data.ministries);
			closeModal();
		} catch (err) {
			setError(err.message || 'Failed to create ministry');
		}
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setFormData({
			name: '',
			description: '',
			teamId: '',
			isActive: true,
			inactiveMessage: '',
		});
		setError('');
	};

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				<div className='page-header'>
					<h1>Ministries</h1>
					{isLoading && <LoadingSpinner />}
					{!orgData && <span>No org data</span>}
					{orgData && (
						<button
							className='create-button'
							onClick={() => setIsModalOpen(true)}
							style={{
								backgroundColor:
									orgData?.primaryColor || '#4b95a3',
								padding: '8px 16px',
								borderRadius: '4px',
								border: 'none',
								color: 'white',
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								cursor: 'pointer',
							}}>
							<span className='material-icons'>add</span>
							Create Ministry
						</button>
					)}
				</div>

				{isModalOpen && (
					<div className='announcement-modal-overlay'>
						<div className='announcement-modal'>
							<div className='modal-header'>
								<h2 className='modal-title'>Create Ministry</h2>
							</div>

							{error && (
								<div className='error-message'>{error}</div>
							)}

							<form
								onSubmit={handleSubmit}
								className='modal-form'>
								<div className='form-group'>
									<label className='form-label'>Name</label>
									<input
										type='text'
										value={formData.name}
										onChange={(e) =>
											setFormData({
												...formData,
												name: e.target.value,
											})
										}
										className='form-input'
										required
									/>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Description
									</label>
									<textarea
										value={formData.description}
										onChange={(e) =>
											setFormData({
												...formData,
												description: e.target.value,
											})
										}
										className='form-textarea'
										required
									/>
								</div>

								<div className='form-group'>
									<label className='form-label'>Team</label>
									<select
										value={formData.teamId}
										onChange={(e) =>
											setFormData({
												...formData,
												teamId: e.target.value,
											})
										}
										className='form-input'>
										<option value=''>Select a team</option>
										{teams.map((team) => (
											<option
												key={team.id}
												value={team.id}>
												{team.name}
											</option>
										))}
									</select>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										<input
											type='checkbox'
											checked={formData.isActive}
											onChange={(e) =>
												setFormData({
													...formData,
													isActive: e.target.checked,
												})
											}
										/>
										Active
									</label>
								</div>

								{!formData.isActive && (
									<div className='form-group'>
										<label className='form-label'>
											Inactive Message
										</label>
										<input
											type='text'
											value={formData.inactiveMessage}
											onChange={(e) =>
												setFormData({
													...formData,
													inactiveMessage:
														e.target.value,
												})
											}
											className='form-input'
											required
										/>
									</div>
								)}

								<div className='modal-actions'>
									<button
										type='button'
										onClick={closeModal}
										className='modal-button modal-button-secondary'>
										Cancel
									</button>
									<button
										type='submit'
										className='modal-button modal-button-primary'>
										Create Ministry
									</button>
								</div>
							</form>
						</div>
					</div>
				)}

				<div className='dashboard-grid'>
					{!orgData ? (
						<p>Please select an organization to view ministries.</p>
					) : ministries.length === 0 && !isLoading ? (
						<p>
							No ministries found. Please create a ministry to get
							started.
						</p>
					) : (
						ministries.map((ministry) => (
							<Link
								key={ministry.id}
								to={`/dashboard/ministries/${ministry.id}`}
								className='dashboard-card'
								style={{
									'--primary-color':
										orgData?.primaryColor || '#4b95a3',
									'--primary-color-light': `${
										orgData?.primaryColor || '#4b95a3'
									}1A`,
								}}>
								<span className='material-icons card-icon'>
									church
								</span>
								<h3>{ministry.name}</h3>
							</Link>
						))
					)}
				</div>
			</div>
		</DashboardLayout>
	);
}

export default DashboardMinistries;
